import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider, SignedIn, RedirectToSignIn } from '@clerk/clerk-react';
import { Analytics } from '@vercel/analytics/react';
import Dashboard from './pages/Dashboard';
import Search from './pages/Search';
import Leads from './pages/Leads';
import BusinessDetail from './pages/BusinessDetail';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import AuthCallback from './pages/AuthCallback';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import InviteAccept from './pages/InviteAccept';
import Profile from './pages/Profile';
import VerifyEmailRequired from './pages/VerifyEmailRequired';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { useAuthStore } from './store/authStore';
import { ClerkAuthProvider } from './components/ClerkAuthProvider';
import './App.css';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
// Beta: Invite acceptance page is not linked anywhere publicly.
// It requires a valid `?token=` from an admin-generated invite email.

// Disable Clerk for now - use local auth only for testing
// Set to true to enable Clerk when ready
const USE_CLERK = false;

const ProtectedRoute: React.FC<{ children: React.ReactElement; allowIncompleteProfile?: boolean }> = ({
  children,
  allowIncompleteProfile = false,
}) => {
  const location = useLocation();
  const { isAuthenticated, user, meLoading, fetchMe, logout } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && !user && !meLoading) {
      fetchMe().catch(() => {
        // If token is invalid/expired, ensure we stop looping and send user to login.
        logout();
      });
    }
  }, [isAuthenticated, user, meLoading, fetchMe, logout]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  const isProfileComplete = !!user.user_type && !!user.search_country;
  if (!allowIncompleteProfile && !isProfileComplete && location.pathname !== '/profile') {
    return <Navigate to="/profile?onboarding=1" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <HelmetProvider>
      <Analytics />
      {USE_CLERK && clerkPubKey ? (
        <ClerkProvider publishableKey={clerkPubKey}>
          <ClerkAuthProvider>
            <Router>
              <div className="App">
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                  <Route
                    path="/dashboard"
                    element={
                      <SignedIn>
                        <Dashboard />
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <SignedIn>
                        <Search />
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/leads"
                    element={
                      <SignedIn>
                        <Leads />
                      </SignedIn>
                    }
                  />
                  <Route
                    path="/business/:id"
                    element={
                      <SignedIn>
                        <BusinessDetail />
                      </SignedIn>
                    }
                  />
                  <Route path="*" element={<RedirectToSignIn />} />
                </Routes>
              </div>
            </Router>
          </ClerkAuthProvider>
        </ClerkProvider>
      ) : (
        <Router>
          <div className="App">
            <Routes>
              <Route
                path="/"
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />}
              />
              <Route path="/landing" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Email verification pages */}
              <Route path="/verify-email-required" element={<VerifyEmailRequired />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Beta: Invite acceptance (magic-link signup). Public users can't use it without a token. */}
              <Route path="/invite/accept" element={<InviteAccept />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute allowIncompleteProfile>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <Search />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute>
                    <Leads />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/:id"
                element={
                  <ProtectedRoute>
                    <BusinessDetail />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </Router>
      )}
    </HelmetProvider>
  );
}

export default App;
